import React from 'react';

export default function AboutPage() {
  return (
    <div style={{ position: 'relative', zIndex: 1, maxWidth: 680, margin: '0 auto', padding: 'clamp(24px,5vw,48px) clamp(12px,4vw,20px)' }}>

      {/* Hero */}
      <div style={{ marginBottom: 48 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C0392B', fontFamily: "'Space Mono', monospace", marginBottom: 14 }}>
          About Dial Market
        </div>
        <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(24px,4vw,36px)', fontWeight: 700, lineHeight: 1.2, marginBottom: 16, letterSpacing: '-0.02em' }}>
          Built for people who want to know the real number.
        </h1>
        <p style={{ fontSize: 15, color: 'var(--text2)', lineHeight: 1.8 }}>
          The luxury watch market moves fast. Dealers know what your watch is worth. Now you do too.
        </p>
      </div>

      {/* Story */}
      <div style={{ marginBottom: 40 }}>
        <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 600, marginBottom: 14 }}>Why we built this</h2>
        <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.85, marginBottom: 14 }}>
          Every watch collector has been there — you walk into a dealer, they look at your Submariner and offer you $9,000. You take it because you don't know better. A week later you see it listed for $14,500.
        </p>
        <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.85, marginBottom: 14 }}>
          The information gap between what dealers know and what collectors know is where money disappears. Dial Market exists to close that gap.
        </p>
        <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.85 }}>
          We track secondary market prices across Chrono24, WatchCharts, and collector communities in real time — and give you three numbers that actually matter: what your watch sells for today, what a dealer should offer you, and what your insurance should cover.
        </p>
      </div>

      {/* How it works */}
      <div style={{ marginBottom: 40 }}>
        <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 600, marginBottom: 20 }}>How the valuations work</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            {
              icon: 'ti-database',
              title: 'Live market data',
              desc: 'Every time you view a watch, we pull current listings from Chrono24 and WatchCharts. Prices reflect what collectors are actually paying right now — not last year, not stale data.',
            },
            {
              icon: 'ti-adjustments',
              title: 'Real condition adjustments',
              desc: 'Our appraisal engine applies six independent factors — condition, papers & box, service history, modifications, wear, and age. Each adjustment is based on actual secondary market spread data.',
            },
            {
              icon: 'ti-building-store',
              title: 'Dealer trade rates',
              desc: 'Trade values reflect what dealers actually pay — not what they say they pay. High-demand brands like Rolex, Patek, and AP command 70–72% of market. Less liquid brands typically 60–65%.',
            },
            {
              icon: 'ti-shield-check',
              title: 'Insurance replacement cost',
              desc: 'Insurance values are set at 125–140% of current market, with discontinued models (Pepsi, Nautilus 5711, Hulk) carrying a higher premium because replacement cost exceeds market price.',
            },
          ].map(item => (
            <div key={item.title} style={{ display: 'flex', gap: 16, padding: '16px', background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(192,57,43,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className={`ti ${item.icon}`} style={{ fontSize: 18, color: '#C0392B' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, fontFamily: "'Space Grotesk', sans-serif" }}>{item.title}</div>
                <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* What we cover */}
      <div style={{ marginBottom: 40 }}>
        <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 600, marginBottom: 14 }}>What we cover</h2>
        <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.8, marginBottom: 14 }}>
          228 watches across 10 brands — Rolex, Patek Philippe, Audemars Piguet, Vacheron Constantin, A. Lange & Söhne, Omega, Cartier, Richard Mille, and Hublot — spanning every production year from 2000 to 2024.
        </p>
        <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.8 }}>
          We cover specific references and colorways — not just "Submariner" but the Hulk, Starbucks, Kermit, Bluesy, and No-Date across every year they were made. Every nickname, every ref, every year.
        </p>
      </div>

      {/* Pricing note */}
      <div style={{ marginBottom: 40, padding: '20px', background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, fontFamily: "'Space Grotesk', sans-serif" }}>Free to use</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>
          Market search, instant appraisals, and price history are free with no account needed. Pro and Dealer plans unlock portfolio tracking, PDF certificates, bulk lookups, and API access.
        </div>
      </div>

      {/* Contact */}
      <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 32 }}>
        <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 600, marginBottom: 14 }}>Get in touch</h2>
        <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.8, marginBottom: 20 }}>
          Questions, feedback, dealer inquiries, or watch data requests — we read everything.
        </p>
        <a href="mailto:Levdar10475@gmail.com" style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '11px 20px', borderRadius: 8,
          background: 'linear-gradient(135deg, #C0392B, #96281B)',
          color: '#fff', fontSize: 13, fontWeight: 600,
          textDecoration: 'none', fontFamily: "'Space Grotesk', sans-serif",
          boxShadow: '0 4px 14px rgba(192,57,43,0.25)',
        }}>
          <i className="ti ti-mail" />
          Levdar10475@gmail.com
        </a>
      </div>
    </div>
  );
}
